<template>
  <div class="lg:flex min-h-screen"> <!-- New wrapper for desktop layout -->
    <AppDrawer
        v-model="drawerOpen"
        v-if="!isLegalRoute"
    />


    <!-- App container -->
    <div class="flex flex-col  flex-1" >
      <!-- Top AppBar -->
      <div class="flex h-16 items-center justify-between px-2 lg:pl-0"  v-if="!isLegalRoute">
        <div class="flex flex-1 min-w-0 items-center">
            <div v-if="showMenuButton" class="lg:hidden!">
              <md-icon-button @click="drawerOpen = true">
                <md-icon><IconMenu /></md-icon>
              </md-icon-button>
            </div>
            <div v-else class="lg:hidden!">
              <md-icon-button @click="goBack()">
                <md-icon><IconArrowBack /></md-icon>
              </md-icon-button>
            </div>
            <h2 class="flex-1 truncate text-lg xl:text-xl font-semibold ml-4 min-w-0">{{ toolbarTitle }}</h2>
        </div>

        <div class="flex lg:gap-4 items-center pr-1">
          <md-icon-button 
            v-if="weatherDisplayStore.currentLocation?.name && ['Home', 'LocationDetail'].includes(route.name)"
            @click="toggleFavourite()"
            :toggle="true"
            :selected="isCurrentLocationStarred"
          >
            <md-icon slot="selected"><IconStar /></md-icon>
            <md-icon><IconStarOutline /></md-icon>
          </md-icon-button>
          <SearchModal/>
        </div>
      </div>
      <div v-else class="flex flex-1 min-w-0 gap-2 items-center py-2 mx-auto">
        <img src="/icons/icon-192x192.png" alt="Forecaster Logo" class="size-10 lg:size-12" />
        <h1 class="text-2xl lg:text-3xl text-primary font-semibold">Forecaster<span class="text-secondary text-xl">.dev</span></h1>
      </div>
      <!-- Content -->
      <main class="flex-1 flex flex-col"> <!-- No overflow-y-auto here, as parent handles it -->
        <router-view></router-view>
      </main>
    </div>
  </div>
</template>

<script setup>
import {ref, onMounted, onBeforeUnmount, computed, watch, nextTick} from 'vue';
import {useRouter, useRoute} from 'vue-router'; // Import useRouter and useRoute
import {useI18n} from 'vue-i18n';
import AppDrawer from './components/AppDrawer.vue';

import SearchModal from "@/components/SearchModal.vue";
import {usePlacesStore} from './stores/places';
import {useWeatherDisplayStore} from './stores/weatherDisplay';
import {useSettingsStore} from './stores/settings'; // Import settings store

// Material Web Imports
import '@material/web/iconbutton/icon-button.js';
import '@material/web/icon/icon.js';
import '@material/web/button/filled-button.js';

import IconMenu from '~icons/material-symbols/menu-rounded';
import IconArrowBack from '~icons/material-symbols/arrow-back-rounded';
import IconStar from '~icons/material-symbols/star-rounded';
import IconStarOutline from '~icons/material-symbols/star-outline-rounded';

const router = useRouter(); // Initialize router
const route = useRoute(); // Initialize route
const placesStore = usePlacesStore();
const weatherDisplayStore = useWeatherDisplayStore();
const settingsStore = useSettingsStore(); // Initialize settings store
const { t } = useI18n();

const showMenuButton = computed(() => {
  return ['Home', 'LocationDetail'].includes(route.name);
});

function goBack() {
  router.back();
}

// State
const drawerOpen = ref(false);


// Function to update the theme-color meta tag
function updateThemeColor() {
  nextTick(() => {
    const themeColorMeta = document.querySelector('meta[name="theme-color"]');
    if (themeColorMeta) {
      const htmlElement = document.documentElement;
      const computedStyle = getComputedStyle(htmlElement);
      const themeColor = computedStyle.getPropertyValue('--color-theme-color').trim();
      themeColorMeta.setAttribute('content', themeColor);
    }
  });
}

onMounted(() => {
  placesStore.loadPlaces();
});

// Watch for theme changes and update the theme-color
watch(() => settingsStore.theme, () => {
  updateThemeColor();
}, { immediate: true });

function toggleFavourite() {
  const currentPlace = weatherDisplayStore.currentLocation;

  if (placesStore.isPlaceStarred(currentPlace)) {
    placesStore.removePlace(currentPlace);
  } else {
    placesStore.addPlace(currentPlace);
  }
}

const isCurrentLocationStarred = computed(() => {
  const currentPlace = weatherDisplayStore.currentLocation;
  return placesStore.isPlaceStarred(currentPlace);
});

const toolbarTitle = computed(() => {
  switch (route.name) {
    case 'Settings':
      return t('drawer.settings');
    case 'ColorGuide':
      return t('drawer.colorGuide');
    case 'PrivacyPolicy':
      return t('settings.privacy_policy');
    case 'TermsOfService':
      return t('settings.terms_of_service');
    case 'LocationDetail':
    case 'Home':
      if (weatherDisplayStore.currentLocation && weatherDisplayStore.currentLocation.name) {
        return weatherDisplayStore.currentLocation.name;
      } else if (route.name === 'Home') {
        return t('home.currentLocation');
      }
      return t('app.title');
    default:
      return t('app.title');
  }
});

const isLegalRoute = computed(() => {
  return route.path.startsWith('/legal/');
});


</script>